'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue, useStore } from 'jotai';
import { useTranslations } from 'next-intl';

import { FormattedMessage } from '@flipster/i18n';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { flex, grid, hstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { OrderbookDisplayMode, orderbookStateBunja } from '@web/app/[locale]/(web)/trade/_misc/state';
import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { orderbookDataBunja } from './bunja';

const DisplayModeDropdown = createDropdownSelect<OrderbookDisplayMode>();
const TickSizeDropdown = createDropdownSelect<string>();

export const OrderbookHeader = () => {
  const transTrade = useTranslations('Trade.r251104');
  const { displayModeAtom, changeDisplayMode, tickSizeLevelsAtom, selectedTickSizeAtom, changeSelectedTickSize } =
    useBunja(orderbookStateBunja);
  const { symbol, isSpotAtom, symbolInfoExistsAtom } = useBunja(tradeSymbolBunja);
  const { quoteCurrencyAtom, baseCurrencyAtom } = useBunja(orderbookDataBunja);
  const store = useStore();
  const displayMode = useAtomValue(displayModeAtom);
  const tickSizeLevels = useAtomValue(tickSizeLevelsAtom);
  const selectedTickSize = useAtomValue(selectedTickSizeAtom);
  const quoteCurrency = useAtomValue(quoteCurrencyAtom);
  const baseCurrency = useAtomValue(baseCurrencyAtom);
  const isSpot = useAtomValue(isSpotAtom);

  return (
    <>
      <div
        data-drag-handle
        className={hstack({
          gap: 10,
          px: 16,
          pb: 4,
          borderColor: 'border.default.level-1',
          justifyContent: 'space-between',
        })}
      >
        <p
          className={css({
            textStyle: 'Text/L/Strong',
            color: 'content.default.level-5',
          })}
        >
          {transTrade('Order_book')}
        </p>
        {!isSpot && tickSizeLevels.length > 1 && (
          <TickSizeDropdown.Root placement='bottom-end' selected={selectedTickSize} onChange={changeSelectedTickSize}>
            <TickSizeDropdown.Trigger>
              <div
                className={flex({
                  color: 'content.default.level-2',
                  textStyle: 'Text/XS/Regular',
                  align: 'center',
                  gap: 2,
                  cursor: 'pointer',
                })}
              >
                <Formatted.Number value={selectedTickSize} />
                <ChevronDownIcon size={12} />
              </div>
            </TickSizeDropdown.Trigger>
            <TickSizeDropdown.Select portal css={{ maxWidth: 140 }}>
              {tickSizeLevels.map((level) => (
                <TickSizeDropdown.Item
                  key={level}
                  value={level}
                  label={
                    <div className={css({ textStyle: 'Text/XS/Regular' })}>
                      <Formatted.Number value={level} />
                    </div>
                  }
                />
              ))}
            </TickSizeDropdown.Select>
          </TickSizeDropdown.Root>
        )}
      </div>
      <div
        className={grid({
          columns: 3,
          px: 16,
          py: 8,
          textStyle: 'Text/XS/Regular',
          color: 'content.default.level-2',
          gap: 6,
        })}
      >
        <div>
          <FormattedMessage
            id='Autogen_common.r251126.s_Price_quoteCurrency'
            values={{
              quoteCurrency: quoteCurrency,
            }}
          />
        </div>
        <div
          className={css({
            textAlign: 'right',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          })}
        >
          <FormattedMessage
            id='Autogen_common.r251126.s_Size_quoteCurrency'
            values={{
              quoteCurrency: displayMode === 'amount' ? quoteCurrency : baseCurrency,
            }}
          />
        </div>
        <div className={css({ textAlign: 'right' })}>
          <DisplayModeDropdown.Root placement='bottom-end' selected={displayMode} onChange={changeDisplayMode}>
            <DisplayModeDropdown.Trigger>
              <div
                className={flex({
                  color: 'content.default.level-2',
                  textStyle: 'Text/XS/Regular',
                  align: 'center',
                  gap: 2,
                })}
                onClick={() => {
                  // 클릭 시점에만 필요한 값이므로 구독하지 않고 store.get으로 읽는다
                  if (!store.get(symbolInfoExistsAtom)) return;
                  segment.track(TradeEvent.OrderBookUnitDropdownClick, {
                    symbol,
                    current_unit: displayMode === 'amount' ? 'QUOTE' : 'BASE',
                  });
                }}
              >
                <div
                  className={css({
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 66,
                  })}
                >
                  <FormattedMessage
                    id='Autogen_common.r251126.s_Sum_quoteCurrency'
                    values={{
                      quoteCurrency: displayMode === 'amount' ? quoteCurrency : baseCurrency,
                    }}
                  />
                </div>

                <ChevronDownIcon size={12} />
              </div>
            </DisplayModeDropdown.Trigger>
            <DisplayModeDropdown.Select portal css={{ maxWidth: 140 }}>
              <DisplayModeDropdown.Item
                value='qty'
                label={
                  <div className={css({ textStyle: 'Text/XS/Regular' })}>
                    <FormattedMessage
                      id='Autogen_common.r251126.s_Sum_quoteCurrency'
                      values={{
                        quoteCurrency: baseCurrency,
                      }}
                    />
                  </div>
                }
              />
              <DisplayModeDropdown.Item
                value='amount'
                label={
                  <div className={css({ textStyle: 'Text/XS/Regular' })}>
                    <FormattedMessage
                      id='Autogen_common.r251126.s_Sum_quoteCurrency'
                      values={{
                        quoteCurrency: quoteCurrency,
                      }}
                    />
                  </div>
                }
              />
            </DisplayModeDropdown.Select>
          </DisplayModeDropdown.Root>
        </div>
      </div>
    </>
  );
};
